import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/content";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";

const Work = () => {
  const content = useSiteContent();
  const tx = (key, fallback) => content?.texts?.[key] || fallback;
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects")
      .then((res) => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="work-page" className="bg-night pt-32">
      <div className="px-6 sm:px-12 lg:px-20">
        <Reveal>
          <Seo
            title="Lavori selezionati — Nicola Verardo Visual Production"
            description="Progetti raccontati come casi di business: esigenza del cliente, approccio visivo, risultato. Product, architettura, food e motion."
            path="/work"
          />
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-fog">{tx("workpage_label", "Work")}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-bone">
            {tx("workpage_title", "Lavori selezionati.")}
          </h1>
          <p className="mt-6 text-base text-fog leading-relaxed max-w-xl">
            {tx("workpage_intro", "Non una galleria di fotografie: una selezione di progetti raccontati come casi di business. Esigenza del cliente, approccio visivo, risultato.")}
          </p>
        </Reveal>
      </div>

      <div className="px-6 sm:px-12 lg:px-20 mt-16 pb-24 space-y-20">
        {loading && (
          <p data-testid="work-loading" className="text-sm font-mono tracking-[0.25em] text-fog">CARICAMENTO…</p>
        )}
        {!loading && projects.length === 0 && (
          <p data-testid="work-empty" className="text-sm text-fog">Nessun progetto disponibile al momento.</p>
        )}
        {projects.map((project, i) => (
          <Reveal key={project.slug}>
            <Link
              to={`/work/${project.slug}`}
              data-testid={`work-project-${project.slug}`}
              className="group grid lg:grid-cols-12 gap-8 items-center"
            >
              <div className={`overflow-hidden lg:col-span-8 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={project.hero}
                  alt={project.title}
                  loading="lazy"
                  className="w-full aspect-[16/9] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="text-xs font-mono tracking-[0.2em] text-gold uppercase">{String(i + 1).padStart(2, "0")} — {project.category}</p>
                <h2 className="mt-4 text-2xl sm:text-3xl font-normal tracking-tight text-bone">{project.title}</h2>
                <p className="mt-2 text-xs font-mono tracking-[0.2em] text-fog uppercase">{project.client} — {project.year}</p>
                <p className="mt-4 text-sm text-fog leading-relaxed">{project.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-bone group-hover:text-gold transition-colors duration-300">
                  {tx("workpage_read", "LEGGI IL CASO")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
        {user && (
          <Link
            to="/admin/progetti/nuovo"
            data-testid="add-project-button"
            className="group flex items-center justify-center gap-3 border border-dashed border-line py-10 text-fog hover:text-gold hover:border-gold transition-[color,border-color] duration-300"
          >
            <Plus size={22} />
            <span className="text-xs font-mono tracking-[0.25em]">AGGIUNGI NUOVO</span>
          </Link>
        )}
      </div>
      <FinalCTA />
    </main>
  );
};

export default Work;
